/**
 * 模拟链路：把一到多台 `SimulatedVehicle` 包装成一条普通的 `Transport`。
 *
 * 上层（会话、车辆运行时、界面）看到的就是一条链路，与串口、WebSocket 没有任何区别。
 * 一条链路里放多台机体是有意的：**多机路由**必须能在模拟机上被测到，
 * 否则"多机支持"只能等有两架真机时才敢验证。
 */
import { BaseTransport, type TransportKind } from '../transport';
import { SimulatedVehicle, type SimulatedFault, type SimulatedVehicleOptions } from './simulated-vehicle';

export interface SimulatedTransportOptions {
  /** 机体数量，默认 1。多机时系统 ID 从 1 开始递增。 */
  readonly count?: number;
  readonly kind?: 'copter' | 'plane';
  readonly version?: 1 | 2;
  readonly now?: () => number;
  /** 物理与出帧的推进间隔，默认 50ms（20Hz）。 */
  readonly tickMs?: number;
  readonly homeLat?: number;
  readonly homeLng?: number;
  readonly homeAltitudeM?: number;
  readonly random?: () => number;
}

export class SimulatedTransport extends BaseTransport {
  readonly kind: TransportKind = 'simulated';
  readonly label: string;

  private readonly drones: SimulatedVehicle[];
  private readonly tickMs: number;
  private readonly now: () => number;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(options: SimulatedTransportOptions = {}) {
    super();
    const count = Math.max(1, options.count ?? 1);
    this.tickMs = options.tickMs ?? 50;
    this.now = options.now ?? (() => Date.now());

    this.drones = Array.from({ length: count }, (_, index) => {
      const vehicleOptions: SimulatedVehicleOptions = {
        systemId: index + 1,
        kind: options.kind ?? 'copter',
        version: options.version ?? 2,
        now: this.now,
        ...(options.homeLat === undefined ? {} : { homeLat: options.homeLat }),
        ...(options.homeLng === undefined ? {} : { homeLng: options.homeLng }),
        ...(options.homeAltitudeM === undefined ? {} : { homeAltitudeM: options.homeAltitudeM }),
        ...(options.random === undefined ? {} : { random: options.random }),
      };
      return new SimulatedVehicle(vehicleOptions);
    });

    this.label =
      count === 1
        ? `模拟机 · 1 架 ${options.kind === 'plane' ? '固定翼' : '多旋翼'}`
        : `模拟机 · ${count} 架`;
  }

  /** 直接访问模拟机体，用于演示与测试中注入状态（真机上没有对应物） */
  get vehicles(): readonly SimulatedVehicle[] {
    return this.drones;
  }

  override async open(): Promise<void> {
    for (const drone of this.drones) {
      drone.connect((bytes) => this.emitData(bytes));
    }
    const startedAt = this.now();
    // 让帧的节奏从一开始就是均匀的：首帧不会被安排在"上次发送时间 = 0"导致的一堆立即发送上
    for (const drone of this.drones) drone.tick(startedAt);

    this.timer = setInterval(() => {
      const at = this.now();
      for (const drone of this.drones) drone.tick(at);
    }, this.tickMs);

    this.setState('open');
  }

  override async close(): Promise<void> {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    for (const drone of this.drones) drone.disconnect();
    this.setState('closed');
  }

  override async write(bytes: Uint8Array): Promise<void> {
    // 一条链路上的所有机体都收到同一串字节：真实的多机链路也是广播语义，
    // 靠帧头里的 target_system 区分，而不是靠物理隔离
    for (const drone of this.drones) drone.receive(bytes);
  }

  /** 注入故障。不指定 `systemId` 时作用于全部机体。 */
  setFault(fault: SimulatedFault, systemId?: number): void {
    for (const drone of this.drones) {
      if (systemId !== undefined && drone.systemId !== systemId) continue;
      drone.setFault(fault);
    }
  }

  /** 让某台机体播报一条状态文本（测告警用） */
  announce(text: string, severity = 6, systemId?: number): void {
    for (const drone of this.drones) {
      if (systemId !== undefined && drone.systemId !== systemId) continue;
      drone.announce(text, severity);
    }
  }
}
